import { RoomControlAssembler } from './RoomControlDelivery';
import type { GoalExplosionSelectionState } from '../game/effects/GoalExplosionSelection';
import { NetworkHub } from '../network/NetworkHub';
import type { BinaryChannel } from '../network/BinaryChannel';
import { immutableGameContentSnapshot } from '../game/content/GameContentContracts';
import { decodeRoomControl, encodeRoomControl, isRoomSystemAnnouncementCode, RECONNECT_WINDOW_MS, ROOM_PROTOCOL_VERSION,
  ROOM_HANDSHAKE_TIMEOUT_MS,
  parseRoomChatEvent, parseRoomGoalExplosionState, parseRoomMusicState, type RoomMusicState,
  type RoomCommand, type RoomEvent, type RoomEventParams, type RoomOperation, type RoomWelcome,
  type RoomWireEvent } from './roomProtocol';

export class RoomClientError extends Error {
  constructor(readonly code: string, readonly params?: RoomEventParams, message = roomErrorFallback(code)) {
    super(message);
    this.name = 'RoomClientError';
  }
}

export interface RoomObserver {
  onSnapshot?(payload: ArrayBuffer): void;
  onEvent?(event: RoomEvent): void;
  onConnection?(connected: boolean): void;
  onError?(error: Error): void;
}

/** Owns transport recovery; physics and presentation only consume its events. */
export class RoomClient {
  readonly #network = new NetworkHub();
  readonly #controlAssembler = new RoomControlAssembler();
  readonly #observers = new Set<RoomObserver>();
  #channel: BinaryChannel | null = null;
  #welcome: RoomWelcome | null = null;
  #snapshot: ArrayBuffer | null = null;
  #music: RoomMusicState | null = null;
  #explosions: GoalExplosionSelectionState | null = null;

  get goalExplosionState(): GoalExplosionSelectionState | null { return this.#explosions; }
  #takeover: Extract<RoomEvent, { type: 'takeover' }> | null = null;
  #takeoverReceivedAt = 0;
  #latency: Extract<RoomEvent, { type: 'latency' }> | null = null;
  #connected = false;
  #completed = false;
  #completionEvent: Extract<RoomEvent, { type: 'closed' }> | null = null;
  #disposed = false;
  #recoveryStarted = 0;
  #retry: ReturnType<typeof setTimeout> | null = null;
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #lastMessageAt = 0;
  #initialResolve: ((welcome: RoomWelcome) => void) | null = null;
  #initialReject: ((error: Error) => void) | null = null;
  #handshake: ReturnType<typeof setTimeout> | null = null;
  readonly #chatEvents: RoomEvent[] = [];
  readonly #chatPending = new Map<string, { resolve: () => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>();

  constructor(readonly url: string, readonly credential: () => Promise<string>, readonly operation: RoomOperation) {}

  get welcome(): RoomWelcome {
    if (!this.#welcome) throw new Error('Room has not accepted this connection.');
    return this.#welcome;
  }
  get connected(): boolean { return this.#connected; }
  get completed(): boolean { return this.#completed; }

  connect(): Promise<RoomWelcome> {
    if (this.#disposed || this.#completed) return Promise.reject(new Error('Room connection is closed.'));
    if (this.#welcome && this.#connected) return Promise.resolve(this.#welcome);
    if (this.#initialResolve) return Promise.reject(new Error('Room connection is already starting.'));
    return new Promise((resolve, reject) => {
      this.#initialResolve = resolve;
      this.#initialReject = reject;
      void this.#open();
    });
  }

  subscribe(observer: RoomObserver): () => void {
    this.#observers.add(observer);
    queueMicrotask(() => {
      if (!this.#observers.has(observer) || this.#disposed) return;
      if (!this.#completed) observer.onConnection?.(this.#connected);
      if (this.#welcome) observer.onEvent?.(this.#welcome);
      if (this.#music) observer.onEvent?.(this.#music);
      if (this.#explosions) observer.onEvent?.(this.#explosions);
      if (this.#takeover) observer.onEvent?.({ ...this.#takeover,
        spectatorSeconds: Math.max(0, this.#takeover.spectatorSeconds
          - (performance.now() - this.#takeoverReceivedAt) / 1000) });
      if (this.#latency) observer.onEvent?.(this.#latency);
      for (const event of this.#chatEvents) observer.onEvent?.(event);
      if (this.#snapshot) observer.onSnapshot?.(this.#snapshot);
      if (this.#completionEvent) observer.onEvent?.(this.#completionEvent);
    });
    return () => this.#observers.delete(observer);
  }

  send(payload: ArrayBuffer): boolean { return this.#connected && Boolean(this.#channel?.send(payload)); }
  sendControl(command: RoomCommand): boolean { return this.send(encodeRoomControl(command)); }

  sendChat(text: string): Promise<void> {
    if (!this.#connected) return Promise.reject(new RoomClientError(this.#completed ? 'match-ended' : 'reconnecting',
      undefined, this.#completed ? roomCloseFallback('match-ended') : 'Reconnecting to the room.'));
    const clientMessageId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#chatPending.delete(clientMessageId);
        reject(new RoomClientError('chat-ack-timeout', undefined, 'The message was not confirmed. Try again.'));
      }, 5_000);
      this.#chatPending.set(clientMessageId, { resolve, reject, timer });
      if (!this.sendControl({ type: 'chat-send', clientMessageId, text })) {
        clearTimeout(timer); this.#chatPending.delete(clientMessageId);
        reject(new RoomClientError('chat-send-failed', undefined, 'The message could not be sent.'));
      }
    });
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#connected = false;
    this.#clearTimers();
    for (const pending of this.#chatPending.values()) {
      clearTimeout(pending.timer);
      pending.reject(new RoomClientError('chat-disconnected', undefined, 'Chat disconnected.'));
    }
    this.#chatPending.clear();
    this.#controlAssembler.reset();
    this.#network.dispose();
    this.#initialReject?.(new Error('Room connection was cancelled.'));
    this.#initialReject = null;
    this.#initialResolve = null;
    this.#observers.clear();
  }

  /** Deliberate departure is distinct from transport/resource disposal. */
  leave(): boolean { return this.sendControl({ type: 'leave' }); }

  async #open(): Promise<void> {
    const previous = this.#channel;
    this.#channel = null;
    previous?.dispose();
    try {
      const token = await this.credential();
      if (this.#disposed) return;
      const channel = this.#network.openWebSocket(this.url, { reconnect: false, maxBufferedBytes: 65_536 });
      this.#channel = channel;
      channel.subscribe({
        onMessage: (payload) => { if (this.#channel === channel) this.#receive(payload); },
        onClose: () => { if (this.#channel === channel) this.#lost(); },
      });
      await channel.connect();
      if (this.#disposed || this.#channel !== channel) return;
      const operation: RoomOperation = this.#welcome ? {
        type: 'resume', roomId: this.#welcome.room.id,
        participantId: this.#welcome.participantId, resumeToken: this.#welcome.resumeToken,
      } : this.operation;
      if (!channel.send(encodeRoomControl({ type: 'authenticate', version: ROOM_PROTOCOL_VERSION, token, operation }))) {
        throw new RoomClientError('room-send-failed', undefined, 'The room request could not be sent.');
      }
      this.#handshake = setTimeout(() => this.#lost(), ROOM_HANDSHAKE_TIMEOUT_MS);
    } catch (error) {
      if (!this.#disposed) this.#lost(error instanceof Error ? error : new Error(String(error)));
    }
  }

  #receive(payload: ArrayBuffer): void {
    this.#lastMessageAt = performance.now();
    let control = decodeRoomControl(payload);
    if (control) {
      try { control = this.#controlAssembler.accept(control); } catch {
        this.#fatal(new RoomClientError('invalid-room-response')); return;
      }
      if (!control) return;
    } else if (this.#controlAssembler.pending) {
      this.#fatal(new RoomClientError('invalid-room-response')); return;
    }
    if (!control) {
      if (!this.#connected) return;
      this.#snapshot = payload;
      for (const observer of this.#observers) observer.onSnapshot?.(payload);
      return;
    }
    if (typeof control.type !== 'string'
      || (control.type === 'error' && (typeof control.code !== 'string' || typeof control.fatal !== 'boolean'))
      || (control.type === 'closed' && typeof control.code !== 'string')
      || (control.type === 'system-announcement' && (!isRoomSystemAnnouncementCode(control.code)
        || typeof control.id !== 'string' || !control.id
        || !Number.isSafeInteger(control.sequence) || (control.sequence as number) < 0
        || typeof control.channelId !== 'string' || !control.channelId
        || typeof control.createdAt !== 'number' || !Number.isFinite(control.createdAt)))) {
      this.#fatal(new RoomClientError('invalid-room-response'));
      return;
    }
    const wireEvent = control as unknown as RoomWireEvent;
    let event: RoomEvent = normalizeRoomEvent(wireEvent);
    if (event.type === 'welcome' || event.type === 'roster') {
      try {
        event = { ...event, room: {
          ...event.room, snapshot: immutableGameContentSnapshot(event.room.snapshot),
        } } as RoomEvent;
      } catch {
        this.#fatal(new RoomClientError('invalid-match-content')); return;
      }
    }
    if (event.type === 'chat-message' || event.type === 'chat-history') {
      try { event = parseRoomChatEvent(event); } catch {
        this.#fatal(new RoomClientError('invalid-room-response')); return;
      }
    }
    if (event.type === 'explosion-state') {
      try { event = parseRoomGoalExplosionState(event); } catch { this.#fatal(new RoomClientError('invalid-match-content')); return; }
      this.#explosions = event;
    }
    if (event.type === 'music-state') {
      try { event = parseRoomMusicState(event); } catch { return; }
      this.#music = event;
    }
    if (event.type === 'chat-message' || event.type === 'chat-history'
      || event.type === 'chat-policy' || event.type === 'system-announcement') {
      if (event.type === 'chat-history') this.#chatEvents.length = 0;
      if (event.type === 'chat-policy') {
        const old = this.#chatEvents.findIndex((item) => item.type === 'chat-policy');
        if (old >= 0) this.#chatEvents.splice(old, 1);
      }
      this.#chatEvents.push(event);
      if (this.#chatEvents.length > 102) this.#chatEvents.splice(this.#chatEvents[0]?.type === 'chat-history' ? 1 : 0, 1);
    }
    if (event.type === 'chat-ack' || (event.type === 'error' && event.clientMessageId)) {
      const id = event.clientMessageId!;
      const pending = this.#chatPending.get(id);
      if (pending) {
        clearTimeout(pending.timer); this.#chatPending.delete(id);
        if (event.type === 'chat-ack') pending.resolve();
        else pending.reject(new RoomClientError(event.code, event.params, event.message));
      }
    }
    if (event.type === 'welcome') {
      if (event.version !== ROOM_PROTOCOL_VERSION || !event.room || !Array.isArray(event.room.participants)
        || typeof event.resumeToken !== 'string' || typeof event.participantId !== 'string') {
        this.#fatal(new RoomClientError('incompatible-room-protocol')); return;
      }
      this.#welcome = event;
      this.#music = null;
      this.#explosions = null;
      this.#takeover = null;
      this.#takeoverReceivedAt = 0;
      this.#latency = null;
      this.#snapshot = null;
      this.#connected = true;
      this.#recoveryStarted = 0;
      if (this.#handshake) clearTimeout(this.#handshake);
      this.#handshake = null;
      if (this.#retry) clearTimeout(this.#retry);
      this.#retry = null;
      if (!this.#heartbeat) this.#heartbeat = setInterval(() => {
        if (!this.#connected) return;
        if (performance.now() - this.#lastMessageAt > 5_000) this.#lost();
        else this.sendControl({ type: 'ping' });
      }, 1_000);
      this.#initialResolve?.(event);
      this.#initialResolve = null;
      this.#initialReject = null;
      for (const observer of this.#observers) observer.onConnection?.(true);
    } else if (event.type === 'roster' && this.#welcome) {
      this.#welcome = { ...this.#welcome, room: event.room };
    } else if (event.type === 'takeover') {
      this.#takeover = event;
      this.#takeoverReceivedAt = performance.now();
    } else if (event.type === 'latency') {
      this.#latency = event;
    } else if (event.type === 'closed' && event.code === 'match-ended'
      && this.#welcome?.room.phase === 'finished' && this.#welcome.room.resultConfirmation === 'confirmed') {
      this.#complete(event); return;
    } else if (event.type === 'closed' || (event.type === 'error' && event.fatal)) {
      this.#fatal(new RoomClientError(event.code, event.params, event.message)); return;
    }
    for (const observer of this.#observers) observer.onEvent?.(event);
  }

  #complete(event: Extract<RoomEvent, { type: 'closed' }>): void {
    this.#completed = true;
    this.#completionEvent = event;
    this.#connected = false;
    this.#clearTimers();
    const channel = this.#channel;
    this.#channel = null;
    channel?.dispose();
    this.#network.dispose();
    for (const pending of this.#chatPending.values()) {
      clearTimeout(pending.timer);
      pending.reject(new RoomClientError('match-ended', undefined, event.message));
    }
    this.#chatPending.clear();
    for (const observer of this.#observers) observer.onEvent?.(event);
  }

  #lost(error: Error = new RoomClientError(
    'connection-interrupted',
    undefined,
    'The room connection was interrupted.',
  )): void {
    if (this.#disposed || this.#completed) return;
    if (this.#handshake) clearTimeout(this.#handshake);
    this.#handshake = null;
    const channel = this.#channel;
    this.#channel = null;
    channel?.dispose();
    this.#controlAssembler.reset();
    if (!this.#welcome) { this.#fatal(error); return; }
    if (this.#connected) {
      this.#connected = false;
      this.#snapshot = null;
      for (const observer of this.#observers) observer.onConnection?.(false);
    }
    this.#recoveryStarted ||= performance.now();
    const elapsed = performance.now() - this.#recoveryStarted;
    const recoverableRanked = this.#welcome.room.settings.mode === 'ranked'
      && (this.operation.type === 'admit' || this.operation.type === 'recover');
    if (!recoverableRanked && elapsed >= RECONNECT_WINDOW_MS) {
      this.#fatal(new RoomClientError('reconnect-expired', undefined, 'The match reconnection window expired.'));
      return;
    }
    if (this.#retry) clearTimeout(this.#retry);
    this.#retry = setTimeout(() => { this.#retry = null; void this.#open(); }, Math.min(2_000, 500 + elapsed / 4));
  }

  #fatal(error: Error): void {
    this.#initialReject?.(error);
    this.#initialReject = null;
    for (const observer of this.#observers) observer.onError?.(error);
    this.dispose();
  }
  #clearTimers(): void {
    if (this.#retry) clearTimeout(this.#retry);
    if (this.#handshake) clearTimeout(this.#handshake);
    if (this.#heartbeat) clearInterval(this.#heartbeat);
    this.#retry = this.#handshake = this.#heartbeat = null;
  }
}

function normalizeRoomEvent(event: RoomWireEvent): RoomEvent {
  if (event.type === 'error') {
    return { ...event, message: roomErrorFallback(typeof event.code === 'string' ? event.code : 'request-failed') };
  }
  if (event.type === 'closed') {
    return { ...event, message: roomCloseFallback(event.code) };
  }
  return event;
}

function roomErrorFallback(code: string): string {
  switch (code) {
    case 'capacity': return 'The server has no capacity for another room.';
    case 'already-exists': return 'This room already exists.';
    case 'unavailable': return 'The room service is unavailable.';
    case 'content-unavailable': return 'Match content is unavailable.';
    case 'invalid-roster': return 'The room roster is invalid.';
    case 'room-not-found': return 'Room not found.';
    case 'admission-closed': return 'This room is not accepting new participants.';
    case 'reservation-expired': return 'The match reservation expired.';
    case 'already-joined': return 'This identity is already in the room.';
    case 'room-full': return 'The room is full.';
    case 'password-required': return 'This room requires a password.';
    case 'invalid-password': return 'The room password is incorrect.';
    case 'kicked': return 'You are temporarily blocked from this room.';
    case 'forbidden': return 'This room operation is not allowed.';
    case 'invalid-phase': return 'This room action is unavailable right now.';
    case 'players-loading': return 'All players must finish loading first.';
    case 'players-not-ready': return 'All players must be ready first.';
    case 'owner-action-required': return 'Transfer ownership or close the room before leaving.';
    case 'invalid-owner': return 'Choose another connected participant as room owner.';
    case 'empty-roster': return 'Add at least one player or bot before starting.';
    case 'invalid-match': return 'The match is invalid.';
    case 'match-not-found': return 'The match is unavailable.';
    case 'lease-mismatch': return 'The match lease is invalid.';
    case 'reservation-unavailable': return 'The requested match slot is unavailable.';
    case 'reservation-conflict': return 'The requested match slot is already reserved.';
    case 'invalid-packet': return 'The match packet is invalid.';
    case 'replacement-unavailable': return 'Replacement is unavailable.';
    case 'replacement-ineligible': return 'This participant is not eligible for the replacement slot.';
    case 'forfeit-unavailable': return 'Forfeit voting is not available yet.';
    case 'chat-forbidden': return 'Chat messages are disabled.';
    case 'resume-expired': return 'The room session could not be resumed.';
    case 'not-member': return 'This connection no longer belongs to the room.';
    case 'invalid-config': return 'The room configuration is invalid.';
    case 'unsupported-bot': return 'This bot is not available for this room.';
    case 'invalid-lease': return 'The match lease is invalid.';
    case 'invalid-reservation': return 'The match reservation is invalid.';
    case 'auth-pending': return 'Authentication is already in progress.';
    case 'authentication-required': return 'A compatible authenticated room connection is required.';
    case 'invalid-operation': return 'The room operation is invalid.';
    case 'rate-limited': return 'Too many room requests. Try again shortly.';
    case 'request-in-progress': return 'Wait for the previous request to finish.';
    case 'invalid-room-response': return 'The room returned an invalid response.';
    case 'invalid-match-content': return 'The room returned invalid match content.';
    case 'incompatible-room-protocol': return 'The room protocol or configuration is incompatible.';
    default: return 'The room request could not be completed.';
  }
}

function roomCloseFallback(code: Extract<RoomWireEvent, { type: 'closed' }>['code']): string {
  switch (code) {
    case 'entry-timeout': return 'The match entry window expired.';
    case 'simulation-start-failed': return 'The match simulation could not be started.';
    case 'invalid-reservation': return 'The match reservation was invalid.';
    case 'incomplete-teams': return 'The match could not start with incomplete teams.';
    case 'lease-expired': return 'The match lease expired.';
    case 'lease-unavailable': return 'The match lease became unavailable.';
    case 'match-start-failed': return 'The match could not be started.';
    case 'match-cancelled': return 'The match was cancelled.';
    case 'server-shutdown': return 'The match server is shutting down.';
    case 'kicked': return 'You were removed from the room.';
    case 'owner-closed': return 'The room owner closed the room.';
    case 'empty-room': return 'The empty room was closed.';
    case 'simulation-ended': return 'The room simulation ended.';
    case 'match-ended': return 'The match has ended.';
    default: return 'The room connection was closed.';
  }
}
